<script setup lang="ts">
import { map, sumBy } from "lodash-es";
import moment from "moment-timezone";
import { useFormat } from "~/composables/use-format";
import RevenueReport from "./RevenueReport.vue";
import CostReport from "./CostReport.vue";

defineOptions({ name: "FinanceReport" });

const { t } = useI18n();
const { c } = useFormat();
const { $api } = useNuxtApp();
const route = useRoute();
const toast = useToast();
const { set: setTitle } = usePageTitle();

const tabs = computed(() => [
  { value: "pnl", label: t("finance.report.tabPnl") },
  { value: "revenue", label: t("finance.report.tabRevenue") },
  { value: "costs", label: t("finance.report.tabCosts") },
]);
const activeTab = computed({
  get: () => {
    const tab = String(route.query.tab || "pnl");
    return tabs.value.some((tb) => tb.value === tab) ? tab : "pnl";
  },
  set: (tab) => navigateTo({ query: { ...route.query, tab } }),
});

// Tabs render every panel at once, so the revenue and cost reports would fire
// their seven calls on mount. They mount the first time their tab is opened.
const visitedTabs = reactive(new Set<string>());
watch(activeTab, (tab) => visitedTabs.add(tab), { immediate: true });

interface FinancialSeriesRecord {
  id: string;
  label: string;
  start?: number;
  revenue: number;
  costPaid: number;
  costPlanned: number;
  net: number;
  cashBalance: number;
}
interface FinancialAnalytics {
  overview: {
    currency: string;
    name: string;
    openingBalance: number;
    openingMonth: string;
    cashBalance: number;
    burn: number;
    runway: number;
  };
  series: FinancialSeriesRecord[];
}

const timezone = "Asia/Ho_Chi_Minh";
// Matches the picker's "Last 90 days" preset exactly, so the field shows
// that label and leaves its row active.
const filter = reactive({
  from: moment.tz(timezone).subtract(89, "days").startOf("day").unix(),
  to: moment.tz(timezone).endOf("day").unix(),
  groupBy: "month",
});
// Calendar-aligned, unlike the picker's own rolling day windows: comparing two
// periods only means anything when both are whole months. Twelve is the
// ceiling — the revenue and usage pipelines walk a day at a time and reject a
// range past 367 days.
const presets = computed(() => {
  const monthsBack = (count: number): [number, number] => [
    moment.tz(timezone).subtract(count - 1, "months").startOf("month").unix(),
    moment.tz(timezone).endOf("day").unix(),
  ];
  return [
    { label: t("dateRange.last3Months"), getValue: () => monthsBack(3) },
    { label: t("dateRange.last6Months"), getValue: () => monthsBack(6) },
    { label: t("dateRange.last12Months"), getValue: () => monthsBack(12) },
  ];
});

const query = computed(() => ({
  ...(filter.from ? { from: filter.from } : {}),
  ...(filter.to ? { to: filter.to } : {}),
  groupBy: filter.groupBy,
}));

// "" = consolidated in USD; a currency = that cash book only.
const currency = ref("");
const currencyOptions = computed(() => [
  { value: "", label: t("finance.consolidated") },
  { value: "USD", label: "USD" },
  { value: "VND", label: "VND" },
]);

const firstLoading = ref(true);
// firstLoading only ever falls once; this one tracks every refetch too, so a
// filter change shows the charts working instead of sitting on stale numbers.
const loading = ref(false);
const data = ref<FinancialAnalytics | null>(null);

const scopeCurrency = computed(() => data.value?.overview.currency ?? "USD");
const netTotal = computed(() => sumBy(data.value?.series ?? [], "net"));
const now = new Date();
const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

const pnlChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: [t("finance.chart.revenue"), t("finance.chart.costPaid"), t("finance.report.net")] },
  xAxis: { type: "category", data: map(data.value?.series ?? [], "label") },
  yAxis: { type: "value" },
  series: [
    { name: t("finance.chart.revenue"), type: "bar", data: map(data.value?.series ?? [], "revenue") },
    { name: t("finance.chart.costPaid"), type: "bar", data: map(data.value?.series ?? [], "costPaid") },
    { name: t("finance.report.net"), type: "line", data: map(data.value?.series ?? [], "net") },
  ],
}));

async function load() {
  loading.value = true;
  try {
    data.value = await $api<FinancialAnalytics>("/analytics/financial", {
      query: { ...query.value, currency: currency.value },
    });
    firstLoading.value = false;
  } catch {
    toast.error({ title: t("message.loadFailed") });
  } finally {
    loading.value = false;
  }
}

watch([query, currency], () => {
  load();
});

onMounted(async () => {
  setTitle(t("finance.report.title"));
  await load();
});
</script>

<template>
  <Tabs v-model="activeTab" :items="tabs">
    <template #pnl>
      <div class="flex flex-col gap-xsmall md:flex-row md:items-center md:justify-between">
        <FormSelect v-model="currency" :options="currencyOptions" size="sm" />
        <FormDateRange
          v-model:from="filter.from"
          v-model:to="filter.to"
          :timezone="timezone"
          :presets="presets"
          size="sm"
        />
      </div>

      <div class="grid grid-cols-2 gap-medium md:grid-cols-4">
        <MetricCard
          :loading="firstLoading"
          :label="t('finance.report.net')"
          :value="c(netTotal, scopeCurrency)"
          :intent="netTotal < 0 ? 'negative' : 'positive'"
        />
        <MetricCard
          :loading="firstLoading"
          :label="t('finance.kpi.cashBalance')"
          :value="c(data?.overview.cashBalance ?? 0, scopeCurrency)"
        />
        <MetricCard
          :loading="firstLoading"
          :label="t('finance.kpi.burn')"
          :value="c(data?.overview.burn ?? 0, scopeCurrency)"
        />
        <MetricCard
          :loading="firstLoading"
          :label="t('finance.kpi.runway')"
          :value="(data?.overview.runway ?? -1) < 0 ? t('finance.kpi.runwayInfinite') : t('finance.kpi.runwayMonths', { count: Math.floor(data?.overview.runway ?? 0) })"
        />
      </div>

      <SectionCard :title="t('analytics.chart.revenueVsCosts')">
        <Chart :option="pnlChartOption" :loading="loading" :height="300" />
      </SectionCard>

      <Card v-if="firstLoading || data?.series.length">
        <DataTable
          :columns="[
            { key: 'id', label: t('finance.report.month'), class: 'min-w-[112px]' },
            { key: 'revenue', label: t('finance.chart.revenue'), align: 'right', class: 'min-w-[144px]' },
            { key: 'costPaid', label: t('finance.chart.costPaid'), align: 'right', class: 'min-w-[144px]' },
            { key: 'costPlanned', label: t('finance.chart.costPlanned'), align: 'right', class: 'min-w-[144px] hidden md:table-cell' },
            { key: 'net', label: t('finance.report.net'), align: 'right', class: 'min-w-[144px]' },
            { key: 'cashBalance', label: t('finance.report.closingBalance'), align: 'right', class: 'min-w-[160px]' },
          ]"
          :items="data?.series ?? []"
          row-key="id"
        >
          <!-- months past today are the planned tail, so they read muted -->
          <template #cell-id="{ row }">
            <span class="content-neutral-strong" :class="{ 'content-neutral-medium': row.id > currentMonthKey }">
              {{ row.id }}
            </span>
          </template>
          <template #cell-revenue="{ row }">
            {{ c(row.revenue, scopeCurrency) }}
          </template>
          <template #cell-costPaid="{ row }">
            {{ c(row.costPaid, scopeCurrency) }}
          </template>
          <template #cell-costPlanned="{ row }">
            <span class="content-neutral-medium">{{ c(row.costPlanned, scopeCurrency) }}</span>
          </template>
          <template #cell-net="{ row }">
            <span :class="row.net < 0 ? 'content-negative' : 'content-positive'">
              {{ c(row.net, scopeCurrency) }}
            </span>
          </template>
          <template #cell-cashBalance="{ row }">
            <span class="content-neutral-strong">{{ c(row.cashBalance, scopeCurrency) }}</span>
          </template>
        </DataTable>
      </Card>
    </template>

    <template #revenue>
      <RevenueReport v-if="visitedTabs.has('revenue')" />
    </template>

    <template #costs>
      <CostReport v-if="visitedTabs.has('costs')" />
    </template>
  </Tabs>
</template>

<style scoped lang="scss">
:deep(.tabs__content) {
  padding: token("padding-medium") 0;
}

:deep(.tabs__content > div) {
  display: flex;
  flex-direction: column;
  gap: token("gap-medium");
}
</style>
